import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SELECT_INVALID_SOLDO_FIELD, collectFormErrors, scrollToFirstResult } from '@shared/formHelpers';
import { Contact, ContactRole, CountryMetadata } from '@shared/models/data-model';
import { RegistrationHelper } from '@shared/registration-helper';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CountryService } from '@shared/services/country.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { LoadingScreenService } from '@shared/services/loading-screen.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from "@shared/services/steps.service";
import { APP_CONFIG, AppConfig } from '@src/app/app-config.module';
import { NGXLogger } from 'ngx-logger';
import { PersonFormGroup, PersonFormHelperService } from '../person-form/person-form-helper.service';

export enum LEGAL_REP_CHOICE {
    NO_CHOICE,
    APPLICANT,
    OTHER
}

interface LegalRepresentativeChoiceFormGroup {
    applicantIsLegalRep: FormControl<LEGAL_REP_CHOICE>;
    legalRep: FormGroup<PersonFormGroup>;
}

@Component({
    selector: 'director',
    templateUrl: './director.component.html',
    styleUrls: ['./director.component.scss']
})
export class DirectorComponent implements OnInit {

    private readonly stepId = REGISTRATION_STEP_ID.DIRECTOR;
    contactsDTO: Contact[] = [];
    form: FormGroup<LegalRepresentativeChoiceFormGroup>;
    loading: boolean = true;
    countryOfIncorporation: string;
    regulatoryInstitution: string;
    registrationProcessURL: string;
    translatedCountries: CountryMetadata[] = [];

    constructor(
        @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
        protected logger: NGXLogger,
        private readonly router: Router,
        private readonly fb: FormBuilder,
        private readonly personFormHelper: PersonFormHelperService,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly countryService: CountryService,
        private readonly translate: TranslateService,
        private readonly loadingScreenService: LoadingScreenService,
        private readonly destroyRef: DestroyRef
    ) { }

    public get applicantContact(): Contact {
        return this.contactsDTO.find(c => c.roles.includes(ContactRole.Applicant));
    }

    public get legalRepresentative(): Contact {
        return this.contactsDTO.find(c => c.roles.includes(ContactRole.LegalRepresentative));
    }

    ngOnInit(): void {
        this.contactsDTO = this.route.snapshot.data.contacts;
        const company = this.route.snapshot.data.company;
        this.countryOfIncorporation = company.registeredAddress?.countryISO;
        this.regulatoryInstitution = this.appConfig.regulatoryInstitutionShorthand[company.eMoneyLicenseIssuer];

        this.registrationProcessURL = this.appConfig.registrationProcessHelpURLs[this.countryOfIncorporation]
            || this.appConfig.registrationProcessHelpURLs.fallback;

        this.translatedCountries = this.route.snapshot.data.countryList;
        this.translate.onLangChange.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(async () => {
            this.loadingScreenService.show('LOADING_SCREEN.COUNTRY_TRANSLATION');
            this.translatedCountries = await this.countryService.getCurrentCountries();
            this.loadingScreenService.hide();
        });

        if (this.legalRepresentative) {
            this.initAsExistingLegalRep(this.legalRepresentative);
        } else {
            this.initAsNew();
        }
        this.form.controls.applicantIsLegalRep.valueChanges.subscribe((choice) => {
            if (choice == LEGAL_REP_CHOICE.APPLICANT) {
                this.selectApplicant();
            } else if (choice === LEGAL_REP_CHOICE.OTHER) {
                this.selectNewContact();
            }
        });
        this.loading = false;

    }

    private initAsNew() {
        this.form = this.fb.group({
            applicantIsLegalRep: [LEGAL_REP_CHOICE.NO_CHOICE, (control: AbstractControl) => {
                return (control.value === LEGAL_REP_CHOICE.NO_CHOICE) ? { required: true } : null;
            }],
            legalRep: this.personFormHelper.createEmptyPersonFormGroup(this.countryOfIncorporation)
        });
    }

    private initAsExistingLegalRep(legalRep: Contact) {
        this.form = this.fb.group({
            applicantIsLegalRep: [legalRep.roles.includes(ContactRole.Applicant) ? LEGAL_REP_CHOICE.APPLICANT : LEGAL_REP_CHOICE.OTHER],
            legalRep: this.personFormHelper.createPersonFormGroupForContact(legalRep, this.countryOfIncorporation)
        });
    }

    selectApplicant(): void {
        const legalRepFormGroup = this.personFormHelper.createPersonFormGroupForContact(this.applicantContact, this.countryOfIncorporation);

        this.form.setControl("legalRep", legalRepFormGroup);
    }

    selectNewContact(): void {
        const legalRepFormGroup = this.personFormHelper.createEmptyPersonFormGroup(this.countryOfIncorporation);
        legalRepFormGroup.controls.roles.setValue([]);

        this.form.setControl("legalRep", legalRepFormGroup);
    }

    private async buildContactsToSave(): Promise<Contact[]> {
        this.logger.debug("Legal Representative Values: ", this.form.value.legalRep);
        const legalRepContact = await this.personFormHelper.mapFormValuesToContact(this.form.controls.legalRep);
        const originalContact = this.contactsDTO.find(c => c.id === legalRepContact.id);
        const roles = [ContactRole.LegalRepresentative];
        if (originalContact?.roles?.length > 0) {
            for (let r of originalContact.roles) {
                if (r !== ContactRole.LegalRepresentative) {
                    roles.push(r);
                }
            }
        }
        const contactsToSave: Contact[] = [];
        if (originalContact) {
            contactsToSave.push(RegistrationHelper.mergeContacts(originalContact, legalRepContact, roles));
        } else {
            contactsToSave.push(RegistrationHelper.cloneContact(legalRepContact, roles));
        }

        for (let c of this.contactsDTO) {
            if (c.id !== legalRepContact.id) {
                const roles = c.roles.filter(r => r !== ContactRole.LegalRepresentative);
                if (roles.length > 0) {
                    contactsToSave.push(RegistrationHelper.cloneContact(c, roles));
                }
            }
        }
        return contactsToSave;
    }

    async onSubmit() {
        if (this.form.value.applicantIsLegalRep === LEGAL_REP_CHOICE.NO_CHOICE) {
            this.form.controls.applicantIsLegalRep.markAsTouched();
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form, { applicantIsLegalRep: 'required' }));
            scrollToFirstResult(SELECT_INVALID_SOLDO_FIELD);
            return;
        }

        this.form.markAllAsTouched();
        if (this.form.valid) {
            this.loading = true;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            const contactsToSave = await this.buildContactsToSave();
            this.registrationService.saveAllContacts(contactsToSave).then(() => {
                const legalRep = contactsToSave.find(c => c.roles.includes(ContactRole.LegalRepresentative));
                this.amplitudeService.trackLegalRepresentativeChoice(legalRep.roles.includes(ContactRole.Applicant));
                this.registrationService.completeStep(this.stepId);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            }, (err) => {
                this.logger.trace("FATAL: ", err);
                this.dynatrace.leaveAction(submitActionId);
            });
        } else {
            this.loading = false;
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
            this.logger.warn("form not valid: ", this.form.value, this.form.errors);
            scrollToFirstResult(SELECT_INVALID_SOLDO_FIELD);
        }
    }

    goToPrevious() {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    public get LEGAL_REP_CHOICE() {
        return LEGAL_REP_CHOICE;
    }
}
